import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Tooltip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import dayjs from 'dayjs';
import { useQuery, useQueries } from '@tanstack/react-query';
import { fetchUsers } from '../../api/admin';
import { fetchAllStudents } from '../../api/students';
import { fetchTutorSchedules } from '../../api/schedules';
import { ROLES } from '../../utils/roles';
import { getWeekStartDate } from '../../utils/week';
import PeriodSelector from '../../components/admin/PeriodSelector';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#EDE9FE' }, '&.Mui-focused fieldset': { borderColor: B.primary } },
};

const AdminStudentReportsPage = () => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total');

  const now = dayjs();
  const defaultStart = now.startOf('month').format('YYYY-MM-DD');
  const defaultEnd = now.endOf('month').format('YYYY-MM-DD');

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [periodLabel, setPeriodLabel] = useState(now.format('MMMM YYYY'));

  const handlePeriodChange = (start, end, label) => { setPeriodStart(start); setPeriodEnd(end); setPeriodLabel(label); };

  const monthStart = dayjs(periodStart);
  const monthEnd = dayjs(periodEnd);

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const studentsQuery = useQuery({ queryKey: ['students'], queryFn: fetchAllStudents });

  const tutors = useMemo(() => (usersQuery.data || []).filter((u) => u.role === ROLES.TUTOR), [usersQuery.data]);

  // Fetch schedules for all weeks of the current month for each tutor
  const weeks = useMemo(() => {
    const result = [];
    let w = getWeekStartDate(monthStart);
    while (w.isBefore(monthEnd)) {
      result.push(w.format('YYYY-MM-DD'));
      w = w.add(1, 'week');
    }
    return result;
  }, [monthStart, monthEnd]);

  const scheduleQueries = useQueries({
    queries: tutors.flatMap((tutor) =>
      weeks.map((week) => ({
        queryKey: ['schedule', tutor.id, week],
        queryFn: () => fetchTutorSchedules(tutor.id, week),
        enabled: Boolean(tutor.id),
        staleTime: 60000,
      }))
    ),
  });

  // Build per-student stats from all schedules
  const studentStats = useMemo(() => {
    const map = {};
    const allEvents = scheduleQueries.flatMap((q) => q.data || []);

    // Filter to current month only
    allEvents.forEach((e) => {
      const eventDate = dayjs(e.startTime);
      if (eventDate.isBefore(monthStart) || eventDate.isAfter(monthEnd)) return;

      const sid = e.studentId;
      if (!sid) return;
      if (!map[sid]) map[sid] = { total: 0, completed: 0, cancelled: 0, rescheduled: 0, scheduled: 0, completedDates: [], cancelledDates: [], rescheduledDates: [], scheduledDates: [] };
      map[sid].total++;
      const dateStr = eventDate.format('DD.MM.YYYY');
      if (e.status === 'COMPLETED') { map[sid].completed++; map[sid].completedDates.push(dateStr); }
      if (e.status === 'CANCELLED') { map[sid].cancelled++; map[sid].cancelledDates.push(dateStr); }
      if (e.status === 'RESCHEDULED') { map[sid].rescheduled++; map[sid].rescheduledDates.push(dateStr); }
      if (e.status === 'SCHEDULED') { map[sid].scheduled++; map[sid].scheduledDates.push(dateStr); }
    });
    return map;
  }, [scheduleQueries, monthStart, monthEnd]);

  const students = useMemo(() => {
    let list = (studentsQuery.data || []).map((s) => ({
      ...s,
      stats: studentStats[s.id] || { total: 0, completed: 0, cancelled: 0, rescheduled: 0, scheduled: 0, completedDates: [], cancelledDates: [], rescheduledDates: [], scheduledDates: [] },
    }));

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.name?.toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      if (sortBy === 'total') return b.stats.total - a.stats.total;
      if (sortBy === 'completed') return b.stats.completed - a.stats.completed;
      if (sortBy === 'cancelled') return b.stats.cancelled - a.stats.cancelled;
      if (sortBy === 'rescheduled') return b.stats.rescheduled - a.stats.rescheduled;
      return 0;
    });

    return list;
  }, [studentsQuery.data, studentStats, search, sortBy]);

  const isLoading = usersQuery.isLoading || studentsQuery.isLoading;
  const allLoaded = scheduleQueries.every((q) => !q.isLoading);

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>Звіт: учні</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            {periodLabel} ({dayjs(periodStart).format('DD.MM.YYYY')} — {dayjs(periodEnd).format('DD.MM.YYYY')})
          </Typography>
        </CardContent>
      </Card>

      <PeriodSelector mode="month" periodStart={periodStart} periodEnd={periodEnd} onChange={handlePeriodChange} />

      {/* Search + Sort */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small" placeholder="Пошук за іменем..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#8891A5' }} /></InputAdornment> }}
          sx={{ ...fieldSx, flex: 1, maxWidth: 320 }}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <SortIcon sx={{ fontSize: 18, color: '#8891A5' }} />
          <ToggleButtonGroup
            value={sortBy} exclusive onChange={(_, val) => { if (val) setSortBy(val); }} size="small"
            sx={{
              flexWrap: 'wrap', gap: 0.5,
              '& .MuiToggleButton-root': {
                border: '1px solid #EDE9FE', borderRadius: '20px !important', px: 2, py: 0.5, textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#8891A5',
                '&.Mui-selected': { bgcolor: B.primary, color: '#fff', borderColor: B.primary, '&:hover': { bgcolor: '#3648A0' } },
              },
            }}
          >
            <ToggleButton value="total">За к-стю занять</ToggleButton>
            <ToggleButton value="completed">За проведеними</ToggleButton>
            <ToggleButton value="cancelled">За скасованими</ToggleButton>
            <ToggleButton value="rescheduled">За перенесеними</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Loading */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>
      ) : students.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 5, textAlign: 'center' }}>
            <SchoolIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
            <Typography variant="h6" sx={{ color: B.dark }}>{search ? 'Нічого не знайдено' : 'Учнів немає'}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={cardSx}>
          {!allLoaded && (
            <Box sx={{ px: 2.5, py: 1, bgcolor: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
              <Typography variant="caption" sx={{ color: '#92400E' }}>Завантаження даних розкладу...</Typography>
            </Box>
          )}
          <Stack divider={<Box sx={{ borderBottom: '1px solid #F1F5F9' }} />}>
            {students.map((student, i) => {
              const color = avatarColors[i % avatarColors.length];
              const s = student.stats;
              return (
                <Box
                  key={student.id}
                  sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: '#FAFAFE' },
                  }}
                >
                  {/* Avatar */}
                  <Avatar sx={{ width: 36, height: 36, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 14, border: `2px solid ${color}30`, flexShrink: 0 }}>
                    {student.name?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  {/* Name + tutor */}
                  <Box sx={{ flex: '0 0 180px', minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }} noWrap>{student.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#8891A5' }} noWrap>{student.tutorName || '—'}</Typography>
                  </Box>

                  {/* Stats chips */}
                  <Stack direction="row" spacing={0.75} sx={{ flex: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      icon={<EventIcon sx={{ fontSize: 14 }} />}
                      label={`${s.total} всього`}
                      size="small"
                      sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 11, height: 24, '& .MuiChip-icon': { color: B.primary } }}
                    />
                    <Tooltip
                      arrow
                      title={s.completedDates.length > 0 ? s.completedDates.join(', ') : 'Немає'}
                      slotProps={{ tooltip: { sx: { maxWidth: 320, fontSize: 12, lineHeight: 1.5 } } }}
                    >
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label={`${s.completed} пров.`}
                        size="small"
                        sx={{ bgcolor: '#D1FAE5', color: '#166534', fontWeight: 600, fontSize: 11, height: 24, cursor: 'pointer', '& .MuiChip-icon': { color: '#22C55E' } }}
                      />
                    </Tooltip>
                    {s.cancelled > 0 && (
                      <Tooltip
                        arrow
                        title={s.cancelledDates.join(', ')}
                        slotProps={{ tooltip: { sx: { maxWidth: 320, fontSize: 12 } } }}
                      >
                        <Chip
                          icon={<CancelIcon sx={{ fontSize: 14 }} />}
                          label={`${s.cancelled} скас.`}
                          size="small"
                          sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: 11, height: 24, cursor: 'pointer', '& .MuiChip-icon': { color: '#EF4444' } }}
                        />
                      </Tooltip>
                    )}
                    {s.rescheduled > 0 && (
                      <Tooltip
                        arrow
                        title={s.rescheduledDates.join(', ')}
                        slotProps={{ tooltip: { sx: { maxWidth: 320, fontSize: 12 } } }}
                      >
                        <Chip
                          icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                          label={`${s.rescheduled} перен.`}
                          size="small"
                          sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: 11, height: 24, cursor: 'pointer', '& .MuiChip-icon': { color: '#F59E0B' } }}
                        />
                      </Tooltip>
                    )}
                    {s.scheduled > 0 && (
                      <Tooltip
                        arrow
                        title={s.scheduledDates.join(', ')}
                        slotProps={{ tooltip: { sx: { maxWidth: 320, fontSize: 12 } } }}
                      >
                        <Chip
                          label={`${s.scheduled} запл.`}
                          size="small"
                          sx={{ bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 500, fontSize: 11, height: 24, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Completion % */}
                  <Box sx={{ flex: '0 0 60px', textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                    {s.total > 0 ? (
                      <Typography variant="body2" fontWeight={800} sx={{ color: s.completed / s.total >= 0.8 ? '#22C55E' : s.completed / s.total >= 0.5 ? '#F59E0B' : '#EF4444' }}>
                        {Math.round((s.completed / (s.total - s.cancelled)) * 100) || 0}%
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#C4B5FD' }}>—</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

export default AdminStudentReportsPage;
